import { Link } from 'react-router-dom';
import type { NoteArticle } from '@/mocks/notes';

interface RelatedArticlesProps {
  articles: NoteArticle[];
  currentCategory?: string;
  currentCategoryLabel?: string;
}

export default function RelatedArticles({
  articles,
  currentCategory,
  currentCategoryLabel,
}: RelatedArticlesProps) {
  if (!articles.length) return null;

  const sameCategory = articles.filter((a) => a.category === currentCategory);
  const otherCategory = articles.filter((a) => a.category !== currentCategory);
  const hasMixed = sameCategory.length > 0 && otherCategory.length > 0;

  return (
    <section className="py-16 md:py-24 bg-[#f5f0e8] border-t border-[#ede5d8]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-6 h-px bg-[#b08060]"></div>
              <span className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase">
                繼續閱讀
              </span>
            </div>
            <h2
              className="font-serif-tc text-[#2c2420] leading-[1.3]"
              style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.75rem)' }}
            >
              {hasMixed && currentCategoryLabel
                ? `更多「${currentCategoryLabel}」文章`
                : '你可能也感興趣'}
            </h2>
          </div>
          <Link
            to="/notes"
            className="inline-flex items-center gap-2 text-[0.9375rem] text-[#7a6248] hover:text-[#5a3e22] transition-colors cursor-pointer whitespace-nowrap group"
          >
            瀏覽全部文章
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </Link>
        </div>

        {/* Same-category articles — featured layout for first card */}
        {sameCategory.length > 0 && (
          <div className="mb-10">
            {hasMixed && (
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[0.8rem] tracking-[0.15em] text-[#9a8470] uppercase font-medium">
                  同分類推薦
                </span>
                <div className="flex-1 h-px bg-[#ede5d8]"></div>
              </div>
            )}
            <div className={`grid gap-5 ${sameCategory.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
              {sameCategory.map((article, idx) => (
                <ArticleCard key={article.slug} article={article} featured={idx === 0 && sameCategory.length >= 2} />
              ))}
            </div>
          </div>
        )}

        {/* Other-category articles */}
        {otherCategory.length > 0 && (
          <div>
            {hasMixed && (
              <div className="flex items-center gap-2 mb-5">
                <span className="text-[0.8rem] tracking-[0.15em] text-[#9a8470] uppercase font-medium">
                  其他分類精選
                </span>
                <div className="flex-1 h-px bg-[#ede5d8]"></div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {otherCategory.map((article) => (
                <ArticleCard key={article.slug} article={article} featured={false} />
              ))}
            </div>
          </div>
        )}

        {/* No split (all same or all other) */}
        {!hasMixed && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => (
              <ArticleCard key={article.slug} article={article} featured={false} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface ArticleCardProps {
  article: NoteArticle;
  featured: boolean;
}

function ArticleCard({ article, featured }: ArticleCardProps) {
  return (
    <Link
      to={`/notes/${article.slug}`}
      className={`group block bg-white rounded-xl border border-[#ede5d8] hover:border-[#c8b89a] overflow-hidden transition-all duration-300 cursor-pointer ${featured ? 'sm:col-span-2 lg:col-span-1' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div className={`relative overflow-hidden ${featured ? 'h-52' : 'h-40'}`}>
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        {article.isFeatured && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#5a3e22]/80 text-white text-[0.7rem] font-medium backdrop-blur-sm">
              <i className="ri-star-line text-[0.7rem]"></i>
              精選
            </span>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#f0e8dc] text-[#7a6248] text-[0.72rem] font-medium">
            {article.categoryLabel}
          </span>
          <span className="text-[0.75rem] text-[#a89a88]">
            <i className="ri-time-line mr-0.5"></i>
            {article.readTime} 分鐘
          </span>
        </div>
        <h4
          className="font-serif-tc text-[#2c2420] leading-[1.45] mb-2 group-hover:text-[#7a6248] transition-colors line-clamp-2"
          style={{ fontSize: '1rem' }}
        >
          {article.title}
        </h4>
        <p className="text-[0.875rem] leading-[1.65] text-[#7a6e62] line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-1 mt-4 text-[0.8125rem] text-[#9a8470] group-hover:text-[#7a6248] transition-colors">
          繼續閱讀
          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
        </div>
      </div>
    </Link>
  );
}
